import { WithStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import blueGrey from '@material-ui/core/colors/blueGrey';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Fab from '@material-ui/core/Fab';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Snackbar from '@material-ui/core/Snackbar';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { push } from 'connected-react-router';
import { commonHandleShowErrorMessage } from 'src/actions/common';
import { ICareerInfo } from 'src/actions/search';
import SearchScenarioFactory from 'src/scenario/SearchScenarioFactory';
import { IMessage } from '../actions/message';
import * as Actions from '../actions/questionnaire';
import MessageDialog from '../components/MessageDialog';
import { IRootState } from '../reducers';
import i18n from '../resources/i18n';
import ELearningScenarioFactory, {
	IFetchELearningProgressParam,
	IFetchELearningTreeParam,
} from '../scenario/ELearningScenarioFactory';
import ScenarioFactory, {
	IFetchQuestionnaireParam,
	ISaveQuestionnaireParam,
	IUpdateQuestionnaireParam,
} from '../scenario/QuestionnaireScenarioFactory';
import FormControl from '../utils/FormControlWrapper';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import { Routes, Screens } from '../utils/Routes';
import { funcClose } from '../utils/StartupUtils';
import { IQuestionnaireProps } from './ELearningQuestionnaire';

const styles = (theme: Theme) =>
	createStyles({
		block: {
			marginBottom: theme.spacing(2),
			marginTop: theme.spacing(2),
			padding: theme.spacing(1),
		},
		blockTitle: {
			flexGrow: 1,
			fontSize: '1.2rem',
		},
		buttons: {
			marginTop: theme.spacing(2),
		},
		mustFlg: {
			color: '#FF0000',
			fontWeight: 'bold',
		},
		questionnaire: {
			borderColor: blueGrey[100],
			borderRadius: '1em',
			borderStyle: 'solid',
			marginTop: theme.spacing(1),
			padding: theme.spacing(1),
		},
		questionnaireTitle: {
			fontSize: '1rem',
			marginBottom: theme.spacing(2),
		},
		root: {
			padding: theme.spacing(2),
			wordBreak: 'break-word',
		},
		textArea: {
			width: '100%',
		},
		whiteSpace: {
			whiteSpace: 'normal',
		},
		whiteSpaceMenuItem: {
			whiteSpace: 'normal',
			wordBreak: 'break-word',
		},
		width: {
			width: '100%',
		},
	});

interface IState {
	answers: { [questionnaireID: string]: number | number[] | string };
	disableReportButton: boolean;
	saveResultSnackbar: boolean;
	openErrorMessageDialog: boolean;
	openReportConfirm: boolean;
	openReportResult: boolean;
	previewDisplayTmpSaveBtn: boolean;
	messages: IMessage[];
	careerName: string;
	vendor: string;
}

interface IQuestionCommonProps {
	questionnaire: Actions.IQuestionnaire;
	classes: IOwnProps['classes'];
	value: number | number[] | string;
}

interface IQuestionProps extends IQuestionCommonProps {
	handleChangeSelect: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
	handleChangeRadio: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleChangeCheck: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleChangeText: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
	showErrorMessage: (content: string, replacements?: string[]) => void;
}

interface IQuestionSelectProps extends IQuestionCommonProps {
	handleChangeSelect: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

interface IQuestionRadioProps extends IQuestionCommonProps {
	handleChangeRadio: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface IQuestionCheckProps extends IQuestionCommonProps {
	handleChangeCheck: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface IQuestionTextProps extends IQuestionCommonProps {
	handleChangeText: (
		questionnaireID: string
	) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const AnswerForm = React.memo((props: IQuestionProps) => {
	const {
		questionnaire,
		classes,
		value,
		handleChangeSelect,
		handleChangeRadio,
		handleChangeCheck,
		handleChangeText,
		showErrorMessage,
	} = props;

	try {
		switch (questionnaire.questionnaireType) {
			case Actions.QuestionnaireType.SELECT:
				if (typeof value !== 'number') {
					throw Error(
						`${value}(プルダウンの選択番号)がnumber型ではない。`
					);
				}
				return (
					<QuestionnaireSelect
						classes={classes}
						questionnaire={questionnaire}
						value={value}
						handleChangeSelect={handleChangeSelect}
					/>
				);
			case Actions.QuestionnaireType.RADIO_BUTTON:
				if (typeof value !== 'number' && typeof value !== 'string') {
					throw Error(
						`${value}(ラジオの選択番号)がnumber型でもなく、string型でもない場合。`
					);
				}
				return (
					<QuestionnaireRadio
						classes={classes}
						questionnaire={questionnaire}
						value={value}
						handleChangeRadio={handleChangeRadio}
					/>
				);
			case Actions.QuestionnaireType.CHECK_BOX:
				if (!Array.isArray(value)) {
					throw Error(
						`${value}(複数選択した番号の配列)が配列型ではない。`
					);
				}
				return (
					<QuestionnaireCheck
						classes={classes}
						questionnaire={questionnaire}
						value={value}
						handleChangeCheck={handleChangeCheck}
					/>
				);

			case Actions.QuestionnaireType.TEXT_AREA:
				if (typeof value !== 'string') {
					throw Error(`${value}(入力内容)がstring型ではない。`);
				}
				return (
					<QuestionnaireText
						classes={classes}
						questionnaire={questionnaire}
						value={value}
						handleChangeText={handleChangeText}
					/>
				);
			default:
				return <></>;
		}
	} catch (error) {
		console.error(error);
		showErrorMessage('COM_F_0001');
	}
	return <></>;
});

/**
 * 選択コンポーネント
 * @param questionnaire 質問情報
 */
const QuestionnaireSelect: React.FC<IQuestionSelectProps> = props => {
	const { questionnaire, classes, handleChangeSelect } = props;
	const options = questionnaire.options as Actions.IOption[];
	const qid = questionnaire.questionnaireID;
	const value = (props.value as number) || -1;
	return (
		<form autoComplete="off">
			<FormControl className={classes.width}>
				<Select
					autoWidth={true}
					value={value}
					inputProps={{
						id: qid,
						name: qid,
					}}
					onChange={handleChangeSelect(qid)}
					classes={{
						selectMenu: classes.whiteSpace,
					}}
				>
					<MenuItem value={0}>
						<em>{i18n.t('QuestionnaireNormal_0003')}</em>
					</MenuItem>
					{options.map((option, i) => (
						<MenuItem
							key={i}
							value={option.replyOrder}
							className={classes.whiteSpaceMenuItem}
						>
							{option.replyText}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</form>
	);
};

/**
 * ラジオボタンコンポーネント
 * @param questionnaire 質問情報
 */
const QuestionnaireRadio: React.FC<IQuestionRadioProps> = props => {
	const { questionnaire, classes, handleChangeRadio } = props;
	const options = questionnaire.options as Actions.IOption[];
	const qid = questionnaire.questionnaireID;
	const value = (props.value as number) || -1;
	return (
		<form autoComplete="off">
			<FormControl component="fieldset" className={classes.width}>
				<RadioGroup
					name={questionnaire.questionnaireID}
					onChange={handleChangeRadio(qid)}
					value={value.toString()}
				>
					{options.map((option, j) => {
						const replyOrder = option.replyOrder
							? option.replyOrder.toString()
							: '';
						return (
							<FormControlLabel
								key={j}
								value={replyOrder}
								control={<Radio />}
								label={option.replyText}
							/>
						);
					})}
				</RadioGroup>
			</FormControl>
		</form>
	);
};

/**
 * チェックボックスコンポーネント
 * @param questionnaire 質問情報
 */
const QuestionnaireCheck: React.FC<IQuestionCheckProps> = props => {
	const { questionnaire, classes, handleChangeCheck } = props;
	const options = questionnaire.options as Actions.IOption[];
	const qid = questionnaire.questionnaireID;
	const values = (props.value as number[]) || [];
	return (
		<FormControl component="fieldset" className={classes.width}>
			<FormGroup>
				{options.map((option, i) => {
					const replyOrder = option.replyOrder
						? option.replyOrder.toString()
						: '';
					return (
						<FormControlLabel
							key={i}
							value={replyOrder}
							control={
								<Checkbox
									checked={
										values.find(
											a => a === option.replyOrder
										)
											? true
											: false
									}
								/>
							}
							label={option.replyText}
							onChange={handleChangeCheck(qid)}
						/>
					);
				})}
			</FormGroup>
		</FormControl>
	);
};

/**
 * テキスト記述コンポーネント
 * @param questionnaire 質問情報
 */
const QuestionnaireText: React.FC<IQuestionTextProps> = props => {
	const { questionnaire, classes, handleChangeText } = props;
	const qid = questionnaire.questionnaireID;
	const value = (props.value as string) || '';

	return (
		<form noValidate={true} autoComplete="off">
			<TextField
				className={classes.textArea}
				id={qid}
				rows={3}
				margin="normal"
				multiline={true}
				onChange={handleChangeText(qid)}
				value={value}
			/>
		</form>
	);
};

/**
 * アンケート標準形式
 */
class QuestionnaireNormal extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.state = {
			answers: {},
			careerName: '',
			disableReportButton: false,
			messages: [],
			openErrorMessageDialog: false,
			openReportConfirm: false,
			openReportResult: false,
			previewDisplayTmpSaveBtn: true,
			saveResultSnackbar: false,
			vendor: '',
		};

		this.errorMessageDialogClose = this.errorMessageDialogClose.bind(this);
		this.handleStartReport = this.handleStartReport.bind(this);
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillReceiveProps(nextProps: IProps) {
		if (this.props.blocks === nextProps.blocks) {
			return;
		}

		// 回答データの初期化
		// サーバからのデータに回答が入っていた場合は反映する
		const answers = {};
		for (const block of nextProps.blocks) {
			for (const q of block.questionnaires) {
				switch (q.questionnaireType) {
					case Actions.QuestionnaireType.SELECT:
					case Actions.QuestionnaireType.RADIO_BUTTON:
						answers[q.questionnaireID] = q.selectedReply;
						break;
					case Actions.QuestionnaireType.CHECK_BOX:
						answers[q.questionnaireID] = [];
						for (const o of q.options as Actions.IOption[]) {
							if (o.selected) {
								answers[q.questionnaireID].push(o.replyOrder);
							}
						}
						break;
					case Actions.QuestionnaireType.TEXT_AREA:
						if (q.options && q.options.length) {
							answers[q.questionnaireID] = q.options[0].replyText;
						}
						break;
				}
			}
		}

		this.setState({
			answers,
		});
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		this.fetch();
		if (this.props.assessmentFlg) {
			this.props
				.doFetchCareerDetail(this.props.token, this.props.careerID)
				.then(data => {
					const careerInfo: ICareerInfo = data.careerInfo;
					this.setState({
						careerName: careerInfo.careerTitle || '',
						vendor: careerInfo.vendor || '',
					});
				});
		}
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidUpdate(prevProps: IProps) {
		if (
			prevProps.careerID === this.props.careerID &&
			prevProps.classID === this.props.classID &&
			prevProps.id === this.props.id
		) {
			return;
		}
		// アンケートから違うアンケートに遷移したときここにくる
		this.fetch();
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillUnmount() {
		this.props.clearQuestionnaireParams();
	}

	public render() {
		return (
			<div className={this.props.classes.root}>
				<Typography variant="h6" gutterBottom={true}>
					{this.props.assessmentFlg
						? this.state.careerName
						: this.props.enqueteName}
				</Typography>
				{this.props.assessmentFlg && this.state.vendor && (
					<Typography variant="h6" align={'center'}>
						{i18n.t('QuestionnaireNormal_0001', {
							vendor: this.state.vendor,
						})}
					</Typography>
				)}
				{this.props.blocks.map(block => {
					return this.block(block);
				})}
				{this.props.periodJudgment !== 2 && <this.Buttons />}
				<MessageDialog
					open={this.state.openErrorMessageDialog}
					showCancelButton={false}
					title={i18n.t('QuestionnaireNormal_0002')}
					contents={this.state.messages}
					onClose={this.errorMessageDialogClose}
					onClickOk={this.errorMessageDialogClose}
				/>
			</div>
		);
	}

	/**
	 * エラーメッセージクローズ
	 */
	protected errorMessageDialogClose() {
		this.setState({ openErrorMessageDialog: false, messages: [] });
	}

	/*
	 * ブロックコンポーネント
	 * @param block ブロック情報
	 */
	protected block(block: Actions.IQuestionnaireBlock) {
		const { classes } = this.props;
		const flg =
			this.props.scoreItemsRegisterFlag === '1' &&
			this.props.respondentCategoryFlg === '1';
		return (
			<ExpansionPanel defaultExpanded={true}>
				<ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
					<Typography
						variant="h6"
						className={classes.blockTitle}
						gutterBottom={true}
					>
						{!this.props.assessmentFlg && block.blockName}
						{this.props.assessmentFlg &&
							(flg ? block.blockName : this.state.careerName)}
					</Typography>
				</ExpansionPanelSummary>
				<ExpansionPanelDetails>
					<div style={{ width: '100%' }}>
						{block.questionnaires.map(q => this.questionnaire(q))}
					</div>
				</ExpansionPanelDetails>
			</ExpansionPanel>
		);
	}

	/**
	 * 質問コンポーネント
	 * @param questionnaire 質問情報
	 */
	protected questionnaire(questionnaire: Actions.IQuestionnaire) {
		const { classes } = this.props;
		return (
			<div className={classes.questionnaire}>
				<Typography variant="h6" className={classes.questionnaireTitle}>
					{questionnaire.mustFlg === 1 && (
						<span className={classes.mustFlg}>＊</span>
					)}
					{questionnaire.questionnaireNo}{' '}
					<span
						dangerouslySetInnerHTML={{
							__html: ReplaceStyleKeywords.replaceStyleKeywords(
								questionnaire.questionnaireText
							),
						}}
					/>
				</Typography>
				<AnswerForm
					classes={classes}
					questionnaire={questionnaire}
					value={this.state.answers[questionnaire.questionnaireID]}
					handleChangeSelect={this.handleChangeSelect}
					handleChangeRadio={this.handleChangeRadio}
					handleChangeCheck={this.handleChangeCheck}
					handleChangeText={this.handleChangeText}
					showErrorMessage={this.props.showMessage}
				/>
			</div>
		);
	}

	/**
	 * 提出ボタン、一時保存ボタン
	 */
	protected Buttons: React.FC = () => {
		return (
			<Grid
				container={true}
				justify="center"
				spacing={3}
				className={this.props.classes.buttons}
			>
				<Grid item={true}>
					<Fab
						color="primary"
						variant="extended"
						size="large"
						onClick={this.handleReport}
						disabled={this.state.disableReportButton}
					>
						{i18n.t('QuestionnaireNormal_0004')}
					</Fab>
				</Grid>
				<Dialog
					open={this.state.openReportConfirm}
					onClose={this.handleCloseConfirmReport}
					aria-labelledby="simple-dialog-title"
				>
					<DialogTitle id="alert-dialog-slide-title">
						{i18n.t('QuestionnaireNormal_0005')}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-slide-description">
							{this.props.assessmentFlg
								? i18n.t('QuestionnaireNormal_0006')
								: i18n.t('QuestionnaireNormal_0007')}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={this.handleStartReport}
							color="primary"
						>
							{i18n.t('QuestionnaireNormal_0008')}
						</Button>
						<Button onClick={this.handleCloseConfirmReport}>
							{i18n.t('QuestionnaireNormal_0009')}
						</Button>
					</DialogActions>
				</Dialog>
				<Dialog
					open={this.state.openReportResult}
					onClose={this.handleCloseReportResult}
					aria-labelledby="simple-dialog-title"
				>
					<DialogTitle id="alert-dialog-slide-title">
						{i18n.t('QuestionnaireNormal_0010')}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-slide-description">
							{this.props.assessmentFlg
								? i18n.t('QuestionnaireNormal_0011')
								: i18n.t('QuestionnaireNormal_0012')}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={this.handleCloseReportResult}
							color="primary"
						>
							{i18n.t('QuestionnaireNormal_0013')}
						</Button>
					</DialogActions>
				</Dialog>
				{((this.props.assessmentFlg &&
					this.props.displayTmpSaveBtn &&
					this.props.flg === 2) ||
					(!this.props.assessmentFlg &&
						this.props.displayTmpSaveBtn &&
						this.state.previewDisplayTmpSaveBtn &&
						this.props.flg === 0)) && (
					<Grid item={true}>
						<Fab
							color="default"
							variant="extended"
							size="large"
							onClick={this.handleSaveQuestionnaire}
							disabled={this.state.disableReportButton}
						>
							{i18n.t('QuestionnaireNormal_0014')}
						</Fab>
						<Snackbar
							anchorOrigin={{
								horizontal: 'left',
								vertical: 'bottom',
							}}
							open={this.state.saveResultSnackbar}
							autoHideDuration={6000}
							onClose={this.handleSaveResultSnackbarClose}
							ContentProps={{
								'aria-describedby': 'message-id',
							}}
							message={
								<span id="message-id">
									{i18n.t('QuestionnaireNormal_0015')}
								</span>
							}
						/>
					</Grid>
				)}
			</Grid>
		);
	};

	/**
	 * 提出ボタン押下ハンドラ
	 */
	protected handleReport = () => {
		this.setState({
			openReportConfirm: true,
		});
	};

	/**
	 * 提出確認ダイアログクローズハンドラ
	 */
	protected handleCloseConfirmReport = () => {
		this.setState({
			openReportConfirm: false,
		});
	};

	/**
	 * 提出開始
	 */
	protected handleStartReport = () => {
		this.setState({
			disableReportButton: true,
			openReportConfirm: false,
		});

		const updatedBlocks = this.makeUpdatedBlocks();
		if (
			this.props.unAnsweredCheck(this.props.mustAnswerFlg, updatedBlocks)
		) {
			this.setState({ disableReportButton: false });
			return;
		}

		const messages: IMessage[] = [];
		for (const ansTest of updatedBlocks) {
			const blockName = ansTest.blockName;
			for (const a1 of ansTest.questionnaires) {
				const questionnaireNo = a1.questionnaireNo;
				if (
					a1.questionnaireType !== undefined &&
					a1.questionnaireType !== null &&
					a1.questionnaireType === '4'
				) {
					if (
						a1.options !== null &&
						a1.options !== undefined &&
						a1.options[0].replyText !== null &&
						a1.options[0].replyText !== undefined &&
						a1.options[0].replyText.length > 2000
					) {
						const mess =
							blockName +
							'　' +
							questionnaireNo.substr(
								0,
								questionnaireNo.length - 1
							) +
							i18n.t('QuestionnaireNormal_0016');
						const message: IMessage = {
							content: 'CHK_E_0004',
							replacements: [mess, '2000'],
						};
						messages.push(message);
						// 表示
						if (messages.length !== 0) {
							this.setState({
								disableReportButton: false,
								messages,
								openErrorMessageDialog: true,
							});
							return;
						}
					}
				}
			}
		}
		if (this.props.preview) {
			this.setState({
				disableReportButton: false,
				openReportResult: true,
			});
			return;
		}
		this.props
			.updateQuestionnaire(this.props.token, {
				blocks: updatedBlocks,
				careerID: this.props.careerID,
				classID: this.props.classID,
				enqueteID: this.props.id,
				isPreview: this.props.preview,
				renewalDate: this.props.renewalDate,
				startTime: this.props.startTime,
				userId: this.props.userAttr.userId,
			})
			.then(result => {
				this.setState({
					disableReportButton: false,
					openReportResult: true,
				});

				if (!this.props.assessmentFlg) {
					// 進捗率更新
					this.props.fetchELearningProgress(this.props.token, {
						careerID: this.props.careerID,
						classID: this.props.classID,
						isPreview: this.props.preview,
						userId: this.props.userAttr.userId,
					});
				}
			});
	};

	/**
	 * 提出確認ダイアログのクローズハンドラ
	 */
	protected handleCloseReportResult = () => {
		const portalFlg = localStorage.getItem('portalFlg');
		if (portalFlg === '0') {
			funcClose();
			if (this.props.assessmentFlg) {
				localStorage.setItem('skillID', this.props.careerID);
				localStorage.setItem('classID', this.props.classID);
			}
			return;
		}
		// this.props.onReported();
		this.setState({
			openReportResult: false,
		});
		if (this.props.preview) {
			this.setState({
				previewDisplayTmpSaveBtn: false,
			});
			return;
		}
		if (!this.props.assessmentFlg) {
			this.props.fetchELearningTree(this.props.token, {
				careerID: this.props.careerID,
				classID: this.props.classID,
				isPreview: this.props.preview,
				userAttr: this.props.userAttr,
			});
		}
		// スキル情報画面に遷移
		if (this.props.assessmentFlg) {
			const to = Routes.getPath(Screens.PERSONAL_SKILL);
			this.props.pushHistory(to);
		} else {
			// アンケート一覧画面に遷移
			if (this.props.onlyQuestionFlg) {
				let to = Routes.getPath(Screens.PERSONAL_QUESTIONNAIRE);
				to = to.replace(/\/:career_id/, `/${this.props.careerID}`);
				to = to.replace(/\/:class_id/, `/${this.props.classID}`);
				this.props.pushHistory(to);
			}
		}
	};

	/**
	 * 一時保存ボタン押下ハンドラ
	 */
	protected handleSaveQuestionnaire = () => {
		this.setState({
			disableReportButton: true,
		});

		const updatedBlocks = this.makeUpdatedBlocks();
		const messages: IMessage[] = [];
		for (const ansTest of updatedBlocks) {
			const blockName = ansTest.blockName;
			for (const a1 of ansTest.questionnaires) {
				const questionnaireNo = a1.questionnaireNo;
				if (
					a1.questionnaireType !== undefined &&
					a1.questionnaireType !== null &&
					a1.questionnaireType === '4'
				) {
					if (
						a1.options !== null &&
						a1.options !== undefined &&
						a1.options[0].replyText !== null &&
						a1.options[0].replyText !== undefined &&
						a1.options[0].replyText.length > 2000
					) {
						const mess =
							blockName +
							'　' +
							questionnaireNo.substr(
								0,
								questionnaireNo.length - 1
							) +
							i18n.t('QuestionnaireNormal_0017');
						const message: IMessage = {
							content: 'CHK_E_0004',
							replacements: [mess, '2000'],
						};
						messages.push(message);
						// 表示
						if (messages.length !== 0) {
							this.setState({
								disableReportButton: false,
								messages,
								openErrorMessageDialog: true,
							});
							return;
						}
					}
				}
			}
		}
		if (this.props.preview) {
			this.setState({
				disableReportButton: false,
				saveResultSnackbar: true,
			});
			return;
		}
		this.props
			.saveQuestionnaire(this.props.token, {
				blocks: updatedBlocks,
				careerID: this.props.careerID,
				classID: this.props.classID,
				enqueteID: this.props.id,
				isPreview: this.props.preview,
				renewalDate: this.props.renewalDate,
				startTime: this.props.startTime,
				userId: this.props.userAttr.userId,
			})
			.then(result => {
				this.setState({
					disableReportButton: false,
					saveResultSnackbar: true,
				});
			});
	};

	/**
	 * 一時保存後のSnackbarクローズハンドラ
	 */
	protected handleSaveResultSnackbarClose = (event: any, reason: any) => {
		if (reason === 'clickaway') {
			return;
		}
		this.setState({ saveResultSnackbar: false });
	};

	/**
	 * 選択コンポーネント変更ハンドラ
	 */
	protected handleChangeSelect = (questionnaireID: string) => (
		event: React.ChangeEvent<HTMLSelectElement>
	) => {
		const target = event.target as HTMLSelectElement;
		this.state.answers[questionnaireID] = target.value;
		this.setState({
			answers: this.state.answers,
		});
	};

	/**
	 * ラジオボタン変更ハンドラ
	 */
	protected handleChangeRadio = (questionnaireID: string) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const target = event.target as HTMLInputElement;
		this.state.answers[questionnaireID] = target.value;
		this.setState({
			answers: this.state.answers,
		});
	};

	/**
	 * チェックボックスクリック時ハンドラ
	 */
	protected handleChangeCheck = (questionnaireID: string) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const target = event.target as HTMLInputElement;
		const value = +target.value;
		if (this.state.answers[questionnaireID]) {
			// すでに回答リストがある場合はそこに追加か削除
			const answers = this.state.answers[questionnaireID] as number[];
			const newAnswers = answers.filter(a => a !== value);
			if (target.checked) {
				newAnswers.push(value);
			}
			this.state.answers[questionnaireID] = newAnswers;
		} else {
			// 回答リストがない場合は新しく作成
			this.state.answers[questionnaireID] = [value];
		}
		this.setState({
			answers: this.state.answers,
		});
	};

	/**
	 * テキスト変更ハンドラ
	 */
	protected handleChangeText = (questionnaireID: string) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const target = event.target as HTMLInputElement;
		this.state.answers[questionnaireID] = target.value;
		this.setState({
			answers: this.state.answers,
		});
	};

	/**
	 * 提出、一時保存用に回答の入力を反映したblocksを作成する
	 */
	protected makeUpdatedBlocks() {
		const updatedBlocks: Actions.IQuestionnaireBlock[] = [];
		for (const fromB of this.props.blocks) {
			const toB: Actions.IQuestionnaireBlock = {
				blockID: fromB.blockID,
				blockName: fromB.blockName,
				blockNo: fromB.blockNo,
				questionnaires: fromB.questionnaires.map(fromQ => {
					const answer = this.state.answers[fromQ.questionnaireID];
					const toQ: Actions.IQuestionnaire = {
						mustFlg: fromQ.mustFlg,
						options: null,
						questionnaireID: fromQ.questionnaireID,
						questionnaireNo: fromQ.questionnaireNo,
						questionnaireText: fromQ.questionnaireText,
						questionnaireType: fromQ.questionnaireType,
					};
					switch (fromQ.questionnaireType) {
						case Actions.QuestionnaireType.SELECT:
						case Actions.QuestionnaireType.RADIO_BUTTON:
							toQ.selectedReply = answer as number;
							break;
						case Actions.QuestionnaireType.CHECK_BOX:
							toQ.options = [];
							for (const fromO of fromQ.options as Actions.IOption[]) {
								const answers = answer as number[];
								const selected = answers.find(
									a => a === fromO.replyOrder
								)
									? true
									: false;
								toQ.options.push({
									replyOrder: fromO.replyOrder,
									replyText: fromO.replyText,
									selected,
								});
							}
							break;
						case Actions.QuestionnaireType.TEXT_AREA:
							const replyText = (answer as string) || '';

							toQ.options = [{ replyText }];
							break;
					}
					return toQ;
				}),
			};
			updatedBlocks.push(toB);
		}
		return updatedBlocks;
	}

	/**
	 * アンケート情報取得
	 */
	protected fetch() {
		this.props.fetchQuestionnaire(this.props.token, {
			careerID: this.props.careerID,
			classID: this.props.classID,
			enqueteID: this.props.id,
			isPreview: this.props.preview,
			userId: this.props.userAttr.userId,
		});
	}
}

const mapStateToProps = (state: IRootState) => ({
	blocks: state.questionnaire.blocks,
	displayTmpSaveBtn: state.questionnaire.displayTmpSaveBtn,
	enqueteName: state.questionnaire.enqueteName,
	mustAnswerFlg: state.questionnaire.mustAnswerFlg,
	periodJudgment: state.questionnaire.periodJudgment,
	renewalDate: state.questionnaire.renewalDate,
	respondentCategoryFlg: state.questionnaire.respondentCategoryFlg,
	scoreItemsRegisterFlag: state.questionnaire.scoreItemsRegisterFlag,
	startTime: state.questionnaire.startTime,

	token: state.session.token,
	userAttr: state.session.userAttr,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	clearQuestionnaireParams: () =>
		dispatch(Actions.clearQuestionnaireParams({})),
	fetchELearningProgress: (
		token: string,
		arg: IFetchELearningProgressParam
	) => {
		const s = ELearningScenarioFactory.createFetchELearningProgress(
			dispatch,
			token
		);
		s.run(arg);
	},
	fetchQuestionnaire: (token: string, arg: IFetchQuestionnaireParam) => {
		const s = ScenarioFactory.createFetchQuestionnaire(dispatch, token);
		s.run(arg);
	},
	saveQuestionnaire: (token: string, arg: ISaveQuestionnaireParam) => {
		const s = ScenarioFactory.createSaveQuestionnaire(dispatch, token);
		return s.run(arg);
	},
	updateQuestionnaire: (token: string, arg: IUpdateQuestionnaireParam) => {
		const s = ScenarioFactory.createUpdateQuestionnaire(dispatch, token);
		return s.run(arg);
	},

	fetchELearningTree: (token: string, arg: IFetchELearningTreeParam) => {
		const s = ELearningScenarioFactory.createFetchELearningTree(
			dispatch,
			token
		);
		s.run(arg);
	},

	doFetchCareerDetail: (token: string, careerID: string) => {
		const s = SearchScenarioFactory.createDoFetchCareerDetail(
			dispatch,
			token
		);
		return s.run(careerID);
	},
	pushHistory: (path: string) => dispatch(push(path)),
	showMessage: (content: string, replacements?: string[]) =>
		dispatch(
			commonHandleShowErrorMessage({
				content,
				replacements,
			})
		),
});

export interface IQuestionnaireNormalProps extends IQuestionnaireProps {
	assessmentFlg: boolean;
}

const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

type IStateProps = ReturnType<typeof mapStateToProps>;
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
type IOwnProps = WithStyles<typeof styles> & IQuestionnaireNormalProps;
type IProps = ReturnType<typeof mergeProps>;

const Connected = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(QuestionnaireNormal);

const Styled = withStyles(styles)(Connected);

export default Styled;



// WEBPACK FOOTER //
// ./src/containers/QuestionnaireNormal.tsx